/**
 * FTX AI 客服聊天 API
 * SSE 流式对话使用 fetch，其他接口使用 request
 */
import request from '@/utils/request'
import { getToken } from '@/utils/auth'

/**
 * SSE 流式发送对话消息
 * 使用 fetch API 实现 SSE 流式读取（axios 不支持流式响应）
 *
 * @param {Object} params - 请求参数
 * @param {string} params.message - 用户消息
 * @param {string|null} params.sessionId - 会话ID（首次可不传）
 * @param {number|null} params.modelId - 模型ID（不传则使用默认模型）
 * @param {boolean|null} params.isReasoning - 是否开启深度思考
 * @param {Array|null} params.images - 图片URL列表
 * @param {Function} onContent - 收到内容片段回调 (content: string)
 * @param {Function} onReasoning - 收到推理片段回调 (content: string)
 * @param {Function} onMeta - 收到元信息回调 (sessionId: string)
 * @param {Function} onRunInfo - 收到运行信息回调 (runId: string)
 * @param {Function} onMetrics - 收到指标回调 (metrics: object)
 * @param {Function} onError - 收到错误回调 (error: string)
 * @param {Function} onDone - 流结束回调
 * @param {AbortSignal} signal - 可选的 AbortController.signal 用于取消
 * @returns {Promise<void>}
 */
export async function sendChatStream({
  message,
  sessionId = null,
  modelId = null,
  isReasoning = null,
  images = null,
}, {
  onContent,
  onReasoning,
  onMeta,
  onRunInfo,
  onMetrics,
  onError,
  onDone,
  signal,
} = {}) {
  const token = getToken()
  const body = {
    message,
    sessionId,
    modelId,
    isReasoning,
    images,
  }

  // 移除 null 值字段
  Object.keys(body).forEach(key => {
    if (body[key] === null || body[key] === undefined) {
      delete body[key]
    }
  })

  const response = await fetch('/api/ftx/chat/send', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(body),
    signal,
  })

  if (!response.ok) {
    // 尝试读取错误信息
    let errMsg = `请求失败 (${response.status})`
    try {
      const errData = await response.json()
      if (errData.msg) errMsg = errData.msg
    } catch (e) { /* ignore */ }
    if (onError) onError(errMsg)
    if (onDone) onDone()
    return
  }

  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''

  try {
    // eslint-disable-next-line no-constant-condition
    while (true) {
      const { done, value } = await reader.read()
      if (done) break

      buffer += decoder.decode(value, { stream: true })
      const lines = buffer.split('\n')
      buffer = lines.pop() // 保留不完整的行

      for (const line of lines) {
        if (!line.trim()) continue
        try {
          const data = JSON.parse(line)
          switch (data.type) {
            case 'content':
              if (onContent) onContent(data.content)
              break
            case 'reasoning':
              if (onReasoning) onReasoning(data.content)
              break
            case 'meta':
              if (onMeta) onMeta(data.session_id)
              break
            case 'run_info':
              if (onRunInfo) onRunInfo(data.run_id)
              break
            case 'metrics':
              if (onMetrics) onMetrics(data.metrics)
              break
            case 'error':
              if (onError) onError(data.error)
              break
          }
        } catch (e) {
          console.warn('[Chat] SSE parse error:', e, line)
        }
      }
    }
  } catch (err) {
    if (err.name === 'AbortError') {
      // 用户主动取消，静默处理
    } else {
      if (onError) onError('网络连接失败，请重试')
    }
  } finally {
    if (onDone) onDone()
  }
}

/**
 * 获取可用 AI 模型列表
 */
export function getChatModels() {
  return request({ url: '/ftx/chat/models', method: 'get' })
}

/**
 * 获取用户对话配置
 */
export function getChatConfig() {
  return request({ url: '/ftx/chat/config', method: 'get' })
}

/**
 * 保存用户对话配置
 */
export function saveChatConfig(data) {
  return request({ url: '/ftx/chat/config', method: 'put', data })
}

/**
 * 获取会话列表
 */
export function getChatSessions() {
  return request({ url: '/ftx/chat/session/list', method: 'get' })
}

/**
 * 获取会话详情（含历史消息）
 */
export function getChatSessionDetail(sessionId) {
  return request({ url: `/ftx/chat/session/${sessionId}`, method: 'get' })
}

/**
 * 删除会话
 */
export function deleteChatSession(sessionId) {
  return request({ url: `/ftx/chat/session/${sessionId}`, method: 'delete' })
}

/**
 * 取消正在运行的对话
 */
export function cancelChatRun(runId) {
  return request({ url: '/ftx/chat/cancel', method: 'post', data: { runId } })
}
